import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Award, CheckCircle, Loader2, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PromotionSuccess = () => {
  const navigate = useNavigate();
  const { token, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    checkPaymentStatus();
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const res = await fetch(`${API}/promotion/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'completed' || data.status === 'paid') {
          setStatus('success');
          await checkAuth();
        } else {
          // Check again after a delay
          setTimeout(checkPaymentStatus, 2000);
        }
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Error:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 flex items-center justify-center p-4" data-testid="promotion-success-page">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 text-yellow-500 mx-auto mb-6 animate-spin" />
              <h1 className="font-heading font-bold text-2xl text-slate-900 mb-2">
                Weryfikacja płatności...
              </h1>
              <p className="text-slate-600">
                Proszę czekać, sprawdzamy status Twojej płatności.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-10 h-10 text-yellow-500" />
              </div>
              <h1 className="font-heading font-bold text-2xl text-slate-900 mb-2">
                Promowanie aktywowane!
              </h1>
              <p className="text-slate-600 mb-6">
                Twoja firma jest teraz promowana na portalu Giełda Budowlana przez 7 dni.
                Pojawi się w sekcji "Firmy godne polecenia" na stronie głównej.
              </p>
              <div className="space-y-3">
                <Button onClick={() => navigate('/')} className="w-full bg-yellow-500 hover:bg-yellow-600">
                  Zobacz stronę główną
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button onClick={() => navigate('/dashboard/company')} variant="outline" className="w-full">
                  Panel firmy
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">❌</span>
              </div>
              <h1 className="font-heading font-bold text-2xl text-slate-900 mb-2">
                Wystąpił problem
              </h1>
              <p className="text-slate-600 mb-6">
                Nie udało się zweryfikować płatności. Spróbuj ponownie lub skontaktuj się z pomocą techniczną.
              </p>
              <Button onClick={() => navigate('/subscription')} className="w-full">
                Powrót do subskrypcji
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PromotionSuccess;
